<template>
    <span
        v-if="menuItems.length > 0"
        class="actions-cell"
    >
        <prime-button
            v-tooltip.bottom="'Actions'"
            icon="pi pi-ellipsis-v"
            text
            rounded
            class="actions-trigger"
            aria-haspopup="true"
            :data-test-id="`row-${rowUrn}-column-${field}-actions-trigger`"
            @click="toggleMenu"
        />
        <prime-menu
            ref="menu"
            :model="menuItems"
            popup
        >
            <template #item="{ item, props }">
                <component
                    :is="item.tag"
                    class="action-item"
                    :class="{
                        'action-item--danger': item.danger,
                        'action-item--disabled': item.disabled,
                    }"
                    :to="item.to"
                    :href="item.href"
                    :target="item.target"
                    :aria-disabled="item.disabled || undefined"
                    :data-test-id="item.testId"
                    v-bind="props.action"
                    @click="handleItemClick(item)"
                >
                    <span class="action-item__icon">
                        <i :class="`icon-${item.icon}`" />
                    </span>
                    <span class="action-item__label">{{ item.label }}</span>
                </component>
            </template>
        </prime-menu>

        <ConfirmDialog
            :group="confirmGroup"
            :pt="confirmDialogPT"
        >
            <template #container="{ message, acceptCallback, rejectCallback }">
                <div
                    class="confirm-container"
                    :data-test-id="`${testIdPrefix}-confirm-container`"
                >
                    <div class="confirm-content">
                        <p
                            class="header"
                            :data-test-id="`${testIdPrefix}-confirm-header`"
                        >
                            {{ message.header }}
                        </p>
                        <p
                            class="body"
                            :data-test-id="`${testIdPrefix}-confirm-message`"
                        >
                            {{ message.message }}
                        </p>
                    </div>
                    <div class="confirm-buttons">
                        <button
                            class="btn"
                            type="button"
                            :data-test-id="`${testIdPrefix}-confirm-cancel`"
                            @click="rejectCallback"
                        >
                            Cancel
                        </button>
                        <button
                            class="btn btn-primary"
                            :class="{ warning: message.danger }"
                            type="button"
                            :data-test-id="`${testIdPrefix}-confirm-accept`"
                            @click="acceptCallback"
                        >
                            {{ message.acceptLabel }}
                        </button>
                    </div>
                </div>
            </template>
        </ConfirmDialog>
    </span>
</template>

<script>
import ConfirmDialog from 'primevue/confirmdialog'
import PrimeButton from 'primevue/button'
import PrimeMenu from 'primevue/menu'
import Tooltip from 'primevue/tooltip'
import canToast from '@/mixins/canToast'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import searchResultActionTypes from '@/constants/search-result-action-types'

const DANGER_ACTIONS = ['delete']

export default {
    name: 'ActionsCell',

    components: {
        ConfirmDialog,
        PrimeButton,
        PrimeMenu,
    },

    directives: {
        tooltip: Tooltip,
    },

    mixins: [canToast, needsOrganizationSlug],

    props: {
        actions: {
            type: Array,
            required: true,
        },
        rowUrn: {
            type: String,
            required: false,
            default: null,
        },
        field: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['action-performed'],

    data() {
        return {
            // Endpoints with a request in flight, so a double click cannot
            // fire the same action twice.
            pendingEndpoints: [],
            confirmDialogPT: {
                root: { style: { border: 'none' } },
                header: { style: { border: 'none' } },
                content: { style: { border: 'none' } },
                footer: { style: { border: 'none' } },
            },
        }
    },

    computed: {
        testIdPrefix() {
            return `row-${this.rowUrn}-column-${this.field}`
        },
        // Every row renders its own dialog, so the group has to be unique to
        // this cell; a shared one would open all of them at once.
        confirmGroup() {
            return `search-action-${this.testIdPrefix}`
        },
        menuItems() {
            return this.actions.map((action) => ({
                label: action.display_name,
                icon: action.icon,
                danger: DANGER_ACTIONS.includes(action.icon) || DANGER_ACTIONS.includes(action.display_name),
                testId: `${this.testIdPrefix}-action-${action.display_name}`,
                action,
                ...this.renderingFor(action),
            }))
        },
    },

    methods: {
        toggleMenu(event) {
            this.$refs.menu.toggle(event)
        },
        closeMenu() {
            this.$refs.menu.hide()
        },
        renderingFor(action) {
            if (action.type === searchResultActionTypes.REQUEST) {
                return {
                    url: null,
                    tag: 'a',
                    to: null,
                    href: null,
                    target: null,
                    disabled: this.isPending(action),
                }
            }

            const url = this.makeUrl(action)
            const openInNewTab = !!action.open_in_new_tab
            const useAnchor = action.type === searchResultActionTypes.EXTERNAL_LINK || openInNewTab

            return {
                url,
                tag: useAnchor ? 'a' : 'router-link',
                to: useAnchor ? null : url,
                href: useAnchor ? url : null,
                target: openInNewTab ? '_blank' : '_self',
                disabled: undefined,
            }
        },
        handleItemClick(item) {
            this.closeMenu()

            const { action } = item ?? {}

            switch (action?.type) {
                case searchResultActionTypes.REQUEST:
                    this.handleRequestAction(action)
                    break
                default:
                    break
            }
        },
        handleRequestAction(action) {
            if (this.isPending(action)) {
                return
            }

            if (action.confirmation_message) {
                this.$confirm.require({
                    group: this.confirmGroup,
                    header: action.confirmation_header,
                    message: action.confirmation_message,
                    acceptLabel: action.display_name,
                    danger: DANGER_ACTIONS.includes(action.icon),
                    accept: () => this.performRequest(action),
                    reject: () => {},
                })
                return
            }

            this.performRequest(action)
        },
        // Whether this action already has a request in flight, so a double
        // click cannot fire the same one twice.
        isPending(action) {
            return this.pendingEndpoints.includes(action.endpoint)
        },
        // Sends the request with the method the action declared, unless one
        // is already in flight for its endpoint.
        sendRequest(action) {
            if (this.isPending(action)) {
                return null
            }

            this.pendingEndpoints.push(action.endpoint)

            const payload = action.payload ?? {}

            if (action.method === 'delete') {
                return this.$http.delete(action.endpoint, { data: payload })
            }

            return this.$http[action.method ?? 'post'](action.endpoint, payload)
        },
        performRequest(action) {
            const request = this.sendRequest(action)

            if (request === null) {
                return Promise.resolve()
            }

            return request
                .then(() => {
                    if (action.success_message) {
                        this.toast(action.success_message)
                    }

                    this.$emit('action-performed', action)
                })
                .catch(() => {
                    this.toast(
                        action.failure_message ?? `Could not ${action.display_name.toLowerCase()} this item`,
                        'error'
                    )
                })
                .finally(() => {
                    this.pendingEndpoints = this.pendingEndpoints.filter((endpoint) => endpoint !== action.endpoint)
                })
        },
        makeUrl(action) {
            if (action.type === searchResultActionTypes.EXTERNAL_LINK) {
                return action.url
            }

            // A slugless page (e.g. the all-users search) has no slug to
            // prefix; interpolating an empty one would produce a
            // protocol-relative "//users/1/edit" rather than a route.
            return this.organizationSlug ? `/${this.organizationSlug}${action.path}` : action.path
        },
    },
}
</script>

<style lang="scss" scoped>
@use 'sass:color';
.actions-cell {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

// Matches the confirmation dialog on the relay details page.
.confirm-container {
    width: 484px;
    max-width: 90vw;
    background-color: #2b2930;
    color: #f1f1f1;
    padding: 24px;
    border-radius: 8px;
    border-width: 0;

    .confirm-content {
        .header {
            font-size: 24px;
            font-family: 'Avenir', sans-serif;
            margin-bottom: 16px;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .body {
            margin-bottom: 24px;
            font-size: 16px;
            font-family: 'Avenir', sans-serif;
        }
    }

    .confirm-buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        padding-top: 12px;
        padding-bottom: 16px;
        width: 100%;
        gap: 12px;

        .warning {
            background-color: #ffb3af;
            border-color: #ffb3af;
            color: #601410;

            &:not([disabled='disabled']):hover,
            &:not([disabled='disabled']):focus {
                background-color: color.adjust(#ffb3af, $lightness: 10%);
                border-color: color.adjust(#ffb3af, $lightness: 10%);
            }
        }
    }
}

.action-item {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0.2rem;
    color: inherit;
    text-decoration: none;

    width: 100%;
    background: none;
    border: none;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &--disabled {
        opacity: 0.6;
        cursor: default;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.06);
        font-size: 1.125rem;

        // Some glyphs hardcode a light color for dark chrome (see
        // _icons.scss, e.g. icon-send); this menu is light, so the item's
        // own color has to win.
        i::before {
            color: inherit;
        }
    }

    &__label {
        font-weight: 500;
        margin-left: 3px;
    }

    &--danger {
        .action-item__icon {
            background: rgba(248, 113, 113, 0.12);
            color: #f87171;
        }

        .action-item__label {
            color: #f87171;
        }
    }
}
</style>
